import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Workflow, { ActionStatus, WorkflowAction, WorkflowCategoryOptions } from 'models/Workflow';
import { WorkflowActionIntent, WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ChevronRight from 'svg/ChevronRight';

import { actionKindMeta, labelForValue } from './actionConfig';
import ExtractionResults from './ExtractionResults';
import PortalRetrievalBody from './PortalRetrievalBody';
import { ReferralModalSection } from './ReferralModal';
import { categoryStatus } from './referralProgress';
import { ActionStatusBadge, actionStatusDisplay } from './StatusBadge';
import StatusIcon, { indicatorColor, statusIndicator } from './StatusIcon';

interface ActionHandlers {
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
  onSaveProgress: (actionId: string, outputs: WorkflowValuesInput) => void;
  isSavingProgress: boolean;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
  // Open the referral modal in edit mode focused on a section (documents / demographics).
  onEdit: (section: ReferralModalSection) => void;
}

// Stable DOM ids so "waiting on" links can scroll to the blocking step.
export const categoryAnchor = (key: string) => `ir-category-${key}`;

// "a", "a and b", "a, b, and c" — same conjunction style as the WaitingOn joiner
// below. (Intl.ListFormat would do this but isn't in the project's ES2020 lib.)
function conjoin(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

interface Props extends ActionHandlers {
  category: WorkflowCategoryOptions;
  actions: WorkflowAction[];
  workflow: Workflow;
}

function actionLabel(action: WorkflowAction): string {
  return action.displayLabel;
}

// Whether ActionBody renders anything for this action — banner, options/form, result, or a
// waiting reason. Terminal/in-flight states (canceled, skipped, running, completed with
// no result) have none, so a single-action category falls back to a status line.
function hasActionDetail(action: WorkflowAction, workflow: Workflow): boolean {
  const meta = actionKindMeta(action.kind);
  if (action.stale || action.isFlagged || action.isPending) return true;
  // Actionable via option buttons, or via a kind-specific form (portal retrieval).
  if (action.isActionRequired && (!!meta?.options || action.kind === 'RetrievePortalInformation')) return true;
  // Portal retrieval also renders while a headless pull is in flight (progress line)
  // and after a failure (the manual form as recovery).
  if (action.kind === 'RetrievePortalInformation') {
    if (action.isErrored) return true;
    if (action.isRunning && workflow.retrievalMode === 'headless') return true;
  }
  return (
    action.isCompleted &&
    (!!meta?.resultValueName ||
      !!meta?.resultSummaryValues ||
      action.kind === 'ExtractAttributes' ||
      action.kind === 'ProcessAttachments')
  );
}

// The category's headline status is a roll-up of its actions (design §12): a person
// needed anywhere wins, then done-when-all-done, else still going.

export default function CategoryCard({ category, actions, workflow, ...handlers }: Props) {
  const single = actions.length === 1;
  // Open the sections that are active or need a person — running, ready-for-review,
  // flagged, or stale — and leave done/pending collapsed.
  const [expanded, setExpanded] = useState(
    actions.some((a) => a.isActionRequired || a.isFlagged || a.stale || a.status === ActionStatus.RUNNING)
  );

  const title = single ? actionLabel(actions[0]) : category.name;
  const subtitle = single ? null : actions.map(actionLabel).join(' • ');

  return (
    <Card id={categoryAnchor(category.key)} data-testid={`category-card-${category.key}`}>
      <HeaderButton type='button' onClick={() => setExpanded((e) => !e)} aria-expanded={expanded} $open={expanded}>
        <HeaderLeft>
          <Caret $open={expanded}>
            <ChevronRight width={12} height={12} />
          </Caret>
          <div>
            <CategoryTitle>{title}</CategoryTitle>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </div>
        </HeaderLeft>
        <ActionStatusBadge status={categoryStatus(actions)} />
      </HeaderButton>

      {expanded &&
        (single ? (
          <SingleBody>
            {hasActionDetail(actions[0], workflow) ? (
              <ActionBody action={actions[0]} workflow={workflow} {...handlers} />
            ) : (
              // No detail to show (e.g. canceled / running) — surface the status so the
              // body isn't empty, consistent with the rows in multi-action categories.
              <StatusOnly>
                <StatusIcon indicator={statusIndicator(actions[0])} size={16} />
                <RowTitle $color={indicatorColor(statusIndicator(actions[0]))}>
                  {actionStatusDisplay(actions[0].status).label}
                </RowTitle>
              </StatusOnly>
            )}
          </SingleBody>
        ) : (
          <Rows>
            {actions.map((action) => (
              <ActionRow key={action.id} data-testid='action-row' data-action-kind={action.kind}>
                <IconCell>
                  <StatusIcon indicator={statusIndicator(action)} size={16} />
                </IconCell>
                <RowMain>
                  <RowTitle $color={indicatorColor(statusIndicator(action))}>
                    {actionStatusDisplay(action.status).label}: {actionLabel(action)}
                  </RowTitle>
                  <ActionBody action={action} workflow={workflow} {...handlers} />
                </RowMain>
              </ActionRow>
            ))}
          </Rows>
        ))}
    </Card>
  );
}

// The per-action content: its description, plus the controls/result/wait reason
// that depend on status. Reused for single- and multi-action categories.
function ActionBody({
  action,
  workflow,
  onComplete,
  isCompleting,
  onSaveProgress,
  isSavingProgress,
  onInvoke,
  isInvoking,
  onEdit,
}: { action: WorkflowAction; workflow: Workflow } & ActionHandlers) {
  const meta = actionKindMeta(action.kind);

  return (
    <>
      {action.stale && <StaleBanner action={action} workflow={workflow} onInvoke={onInvoke} isInvoking={isInvoking} />}

      {action.isFlagged && <FlaggedPrompt action={action} workflow={workflow} />}

      {action.isCompleted && <CompletedResult action={action} workflow={workflow} />}

      {action.isCompleted && action.kind === 'ProcessAttachments' && (
        <CompletedNote>
          Documents have been successfully processed.{' '}
          <InlineLink type='button' onClick={() => onEdit('documents')} data-testid='add-documents'>
            Add additional documents
          </InlineLink>
        </CompletedNote>
      )}

      {action.isCompleted && action.kind === 'ExtractAttributes' && (
        <>
          <CompletedNote>
            Review extracted patient demographics below.{' '}
            <InlineLink type='button' onClick={() => onEdit('demographics')} data-testid='edit-demographics'>
              Edit demographics
            </InlineLink>
          </CompletedNote>
          <ExtractionResults action={action} workflow={workflow} />
        </>
      )}

      {action.isActionRequired && <MidRedoNotice action={action} workflow={workflow} />}

      {action.kind === 'RetrievePortalInformation' && (
        <PortalRetrievalBody
          action={action}
          workflow={workflow}
          onComplete={onComplete}
          isCompleting={isCompleting}
          onSaveProgress={onSaveProgress}
          isSavingProgress={isSavingProgress}
        />
      )}

      {action.isActionRequired && meta?.options && (
        <ActionRequired>
          {meta.prompt && <BodySmall>{meta.prompt}</BodySmall>}
          <Choices>
            {meta.options.map((option) => (
              <Button
                key={option.label}
                variant={option.tone === 'danger' ? 'outline-danger' : 'primary'}
                size='small'
                disabled={isCompleting}
                onClick={() => onComplete(action.id, option.outputs)}
                data-testid={`action-option-${option.label}`}>
                {option.label}
              </Button>
            ))}
          </Choices>
        </ActionRequired>
      )}

      {action.isPending && <WaitingOn action={action} workflow={workflow} />}
    </>
  );
}

// Explain which step(s) a pending action is waiting on, linkable to that step.
function WaitingOn({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const blockers = blockersFor(action, workflow);

  if (blockers.length === 0) {
    return <Muted>Waiting on earlier steps before this can start.</Muted>;
  }

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <Muted data-testid='waiting-on'>
      Waiting on{' '}
      {blockers.map((blocker, i) => (
        <span key={blocker.id}>
          {i > 0 && (i < blockers.length - 1 ? ', ' : blockers.length > 2 ? ', and ' : ' and ')}
          <StepLink type='button' onClick={() => scrollTo(blocker.id)}>
            {blocker.label}
          </StepLink>
        </span>
      ))}
      .
    </Muted>
  );
}

// The not-yet-completed producer action(s) of each missing required value — the
// step(s) that, once done, would unblock this action.
function blockersFor(action: WorkflowAction, workflow: Workflow): { id: string; label: string }[] {
  const missing = action.required.filter((name) => !workflow.valueFor(name)?.value);

  const byId = new Map<string, string>();
  missing.forEach((name) => {
    // The producer is whichever action declares it produces this value (API-provided
    // required/produces — no hardcoded kind→value map on the FE).
    const producer = workflow.actions.find((a) => a.produces.includes(name));
    if (producer && !producer.isCompleted) {
      byId.set(categoryAnchor(producer.categoryKey ?? ''), producer.displayLabel);
    }
  });

  return Array.from(byId, ([id, label]) => ({ id, label }));
}

function StaleBanner({
  action,
  workflow,
  onInvoke,
  isInvoking,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onInvoke: (actionId: string, invokeAction: WorkflowActionIntent) => void;
  isInvoking: boolean;
}) {
  const diff = workflow.inputDiffFor(action);

  return (
    <Banner data-testid='stale-banner'>
      <BannerTitle>Inputs changed since this ran</BannerTitle>
      {diff.length > 0 ? (
        <DiffList>
          {diff.map((d) => (
            <li key={d.name}>
              <DiffName>{labelForValue(workflow, d.name)}</DiffName>: <DiffOld>{d.ranWith || '—'}</DiffOld> →{' '}
              <DiffNew>{d.current || '—'}</DiffNew>
            </li>
          ))}
        </DiffList>
      ) : (
        // Trust the `stale` flag even when we can't enumerate the changed inputs
        // (empty snapshot, or a value the FE can't resolve) — don't over-claim.
        <Muted>Its inputs have changed since it ran.</Muted>
      )}
      <BannerActions>
        <Button
          size='small'
          disabled={isInvoking}
          onClick={() => onInvoke(action.id, 'rerun')}
          data-testid='stale-rerun'>
          Re-run
        </Button>
        <Button
          variant='outline-gray'
          size='small'
          disabled={isInvoking}
          onClick={() => onInvoke(action.id, 'dismiss')}
          data-testid='stale-dismiss'>
          Dismiss
        </Button>
      </BannerActions>
    </Banner>
  );
}

// The mid-redo awareness notice (design §9/§13): a producer of this step's inputs is
// in flight, so the information this step relies on may be about to change. Awareness,
// never a block — the person can still act (the engine never gates the decision).
function MidRedoNotice({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const producers = workflow.inFlightProducersFor(action);
  if (producers.length === 0) return null;

  return (
    <NoticeBanner data-testid='mid-redo-notice'>
      {conjoin(producers.map((producer) => producer.displayLabel))} {producers.length > 1 ? 'are' : 'is'} being redone
      and may change the information this step relies on. You can still act now.
    </NoticeBanner>
  );
}

// A flagged action can't run until a person supplies the values the engine couldn't
// derive. It isn't actionable here (no options) — just surface what's missing.
function FlaggedPrompt({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const names = action.unobtainableValues.map((name) => labelForValue(workflow, name));

  return (
    <Banner data-testid='flagged-prompt'>
      <BannerTitle>More information needed</BannerTitle>
      {names.length > 0 ? (
        <BodySmall>This step needs {conjoin(names)} to continue.</BodySmall>
      ) : (
        <BodySmall>This step needs more information before it can run.</BodySmall>
      )}
    </Banner>
  );
}

// Array values (the attachments packet) summarize as their filenames; scalars render as-is.
function formatSummaryValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((entry) =>
        entry && typeof entry === 'object' ? ((entry as { filename?: string }).filename ?? 'document') : String(entry)
      )
      .join(', ');
  }
  return String(value);
}

function CompletedResult({ action, workflow }: { action: WorkflowAction; workflow: Workflow }) {
  const meta = actionKindMeta(action.kind);
  if (!meta) return null;

  const value = meta.resultValueName ? workflow.valueFor(meta.resultValueName)?.value : null;
  const linkId = meta.resultLink ? workflow.valueFor(meta.resultLink.valueName)?.value : null;
  const summary = (meta.resultSummaryValues ?? [])
    .map((name) => ({ name, value: workflow.valueFor(name)?.value }))
    .filter((entry) => (Array.isArray(entry.value) ? entry.value.length > 0 : entry.value));
  if (!value && summary.length === 0) return null;

  const label = value
    ? (meta.options?.find((o) => o.outputs[meta.resultValueName!] === value)?.label ??
      meta.resultLabels?.[value] ??
      value)
    : null;

  return (
    <Result data-testid='action-result'>
      {label && (
        <>
          <ResultLabel>Result</ResultLabel>
          <ResultValue>
            {label}
            {linkId && meta.resultLink && (
              <ResultLink to={meta.resultLink.path(linkId)} data-testid='result-link'>
                {meta.resultLink.label} →
              </ResultLink>
            )}
          </ResultValue>
        </>
      )}
      {summary.map((entry) => (
        <SummaryRow key={entry.name} data-testid={`result-summary-${entry.name}`}>
          <SummaryName>{labelForValue(workflow, entry.name)}</SummaryName>
          <SummaryValue>{formatSummaryValue(entry.value)}</SummaryValue>
        </SummaryRow>
      ))}
    </Result>
  );
}

const Card = styled.div`
  border: 1px solid ${colors.black15};
  border-radius: 8px;
  background: ${colors.white};
  overflow: hidden;
`;

const HeaderButton = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: ${colors.black03};
  border: none;
  border-bottom: ${({ $open }) => ($open ? `1px solid ${colors.black10}` : 'none')};
  cursor: pointer;
  text-align: left;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;

  > svg {
    flex-shrink: 0;
    margin-top: 4px;
    color: ${colors.black50};
  }
`;

// Single caret that rotates when the section is open — black in both states (the down
// chevron SVG is hardcoded blue, so we rotate the currentColor right chevron instead).
const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: 4px;
  color: ${colors.black50};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 0.15s ease;
`;

const CategoryTitle = styled(BodySmallBold)`
  font-size: 15px;
`;

const Subtitle = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.black50};
`;

const SingleBody = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StatusOnly = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Rows = styled.div`
  background: ${colors.white};
`;

const ActionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 16px 16px 20px;

  & + & {
    border-top: 1px solid ${colors.black05};
  }
`;

const IconCell = styled.span`
  flex-shrink: 0;
  margin-top: 2px;
  display: inline-flex;
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const RowTitle = styled(BodySmallBold)<{ $color?: string }>`
  color: ${({ $color }) => $color ?? colors.black};
`;

const ActionRequired = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Choices = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ResultLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const ResultValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.black};
`;

const ResultLink = styled(Link)`
  margin-left: 8px;
  font-size: 13px;
  font-weight: 400;
  color: ${colors.primaryBlue};
`;

const SummaryRow = styled.div`
  display: flex;
  gap: 8px;
  font-size: 13px;
`;

const SummaryName = styled(BodySmall)`
  color: ${colors.black50};
  flex-shrink: 0;
`;

const SummaryValue = styled(BodySmall)`
  color: ${colors.black};
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

const CompletedNote = styled(BodySmall)`
  display: block;
  color: ${colors.black75};
`;

const InlineLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.primaryBlue};
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
`;

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid ${colors.secondaryOrange40};
  background: ${colors.secondaryOrange10};
`;

// Softer than Banner (orange = needs you): this is awareness only, so it reads as a
// note, not an alarm.
const NoticeBanner = styled(BodySmall)`
  display: block;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${colors.black10};
  background: ${colors.black03};
  color: ${colors.black75};
`;

const BannerTitle = styled(BodySmallBold)`
  color: ${colors.black};
`;

const DiffList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: ${colors.black75};
`;

const DiffName = styled.span`
  font-weight: 700;
`;

const DiffOld = styled.span`
  color: ${colors.black50};
  text-decoration: line-through;
`;

const DiffNew = styled.span`
  font-weight: 700;
  color: ${colors.black};
`;

const BannerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`;

const StepLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: ${colors.primaryBlue};
  cursor: pointer;
  text-decoration: underline;
`;
